import React from "react";
import { Link } from "react-router-dom";
import { Loader2, Printer, RefreshCw, Tag } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { LabelCetak } from "@/components/LabelTermal";
import { useAuth } from "@/context/AuthContext";
import { apiLabelAntrean, apiLabelCetak, apiUpdatePengaturan, formatApiErrorDetail } from "@/lib/api";
import { cssCetak, GRUP, infoCetak } from "@/lib/label";
import * as W from "@/lib/waktu";

// Owner 2026-10-19 (requirement 71, v2.11): the print queue of thermal labels at the QC computer.
// "Cetak" = one click: the label is marked printed on the server (atomic: two clicks/tabs mark it once),
// then Chrome's print dialog opens with a 100×150 mm page — with the label printer as Windows' default
// printer, Enter prints it. A later print of the same label is a reprint (recorded).
const MUAT_MS = 15000;
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export default function AntreanLabel() {
  const { user } = useAuth();
  const owner = user?.role === "admin";
  const [tab, setTab] = React.useState("ANTRE");
  const [d, setD] = React.useState(null);
  const [cetak, setCetak] = React.useState(null);  // the label being printed
  const [pesan, setPesan] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const muat = React.useCallback(async () => {
    try { setD(await apiLabelAntrean(tab)); } catch (e) { setPesan({ ok: false, t: errText(e) }); }
  }, [tab]);
  React.useEffect(() => { muat(); const t = setInterval(muat, MUAT_MS); return () => clearInterval(t); }, [muat]);
  React.useEffect(() => {  // print once the label is drawn
    if (!cetak) return undefined;
    const t = setTimeout(() => { window.print(); }, 300);
    return () => clearTimeout(t);
  }, [cetak]);
  React.useEffect(() => {  // the printed copy stays (off screen) until the next print; the list is refreshed
    window.addEventListener("afterprint", muat);
    return () => window.removeEventListener("afterprint", muat);
  }, [muat]);

  const cetakLabel = async (l) => {
    setBusy(l.id); setPesan(null); setCetak(null);
    try { const r = await apiLabelCetak(l.id); setCetak({ ...r, kali: Date.now() }); setPesan({ ok: true, t: r.jumlah_cetak > 1 ? `Cetak ulang SPK ${r.nomor_spk} (ke-${r.jumlah_cetak})` : `Label SPK ${r.nomor_spk} dicetak` }); }
    catch (e) { setPesan({ ok: false, t: errText(e) }); } finally { setBusy(null); }
  };
  const ubahSaklar = async (g, v) => {
    setBusy(g);
    try { await apiUpdatePengaturan({ label_aktif: { [g]: v } }); await muat(); setPesan({ ok: true, t: `Label ${GRUP[g]} ${v ? "dinyalakan" : "dimatikan"}` }); }
    catch (e) { setPesan({ ok: false, t: errText(e) }); } finally { setBusy(null); }
  };

  return (
    <div data-testid="antrean-label" className="min-h-screen bg-slate-950 text-slate-200">
      <style>{cssCetak()}</style>
      <TopBar />
      <main className="mx-auto max-w-5xl space-y-4 px-4 py-6 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl"><Tag size={24} /> Antrean Label</h1>
            <p className="mt-1 text-sm text-slate-400">Label 100×150 mm untuk printer termal di komputer QC. Label masuk otomatis saat <b>Selesai</b> di pos Outdoor, langkah DTF terakhir, dan <b>QC Jersey Lulus</b>. Tekan <b>Cetak</b>, lalu Enter di dialog cetak (printer label sebagai printer bawaan).</p>
          </div>
          <div className="flex gap-2">
            <Link data-testid="label-uji-link" to="/label/uji" className="rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300 hover:text-amber-300">Uji printer</Link>
            <button onClick={muat} className="inline-flex items-center gap-1 rounded-lg border border-slate-700 px-3 py-1.5 text-sm text-slate-300"><RefreshCw size={14} /> Muat ulang</button>
          </div>
        </div>
        {pesan && <div data-testid="label-pesan" className={`rounded-lg border px-4 py-2.5 text-sm ${pesan.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-red-500/30 bg-red-500/10 text-red-300"}`}>{pesan.t}</div>}
        {owner && d && (
          <div data-testid="label-saklar" className="flex flex-wrap items-center gap-4 rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-3 text-sm">
            <span className="font-semibold text-slate-100">Label otomatis (Owner):</span>
            {Object.keys(GRUP).map((g) => (
              <label key={g} className="inline-flex items-center gap-1.5"><input data-testid={`label-saklar-${g}`} type="checkbox" disabled={busy === g}
                checked={d.aktif[g] !== false} onChange={(e) => ubahSaklar(g, e.target.checked)} /> {GRUP[g]}</label>
            ))}
          </div>
        )}
        <div className="flex gap-2">
          {[["ANTRE", `Antre${d && tab === "ANTRE" ? ` (${d.jumlah_antre})` : ""}`], ["DICETAK", "Sudah dicetak"]].map(([k, l]) => (
            <button key={k} data-testid={`label-tab-${k}`} onClick={() => { setTab(k); setD(null); }}
              className={`rounded-md border px-3 py-1.5 text-sm ${tab === k ? "border-amber-500 bg-amber-500 font-semibold text-slate-950" : "border-slate-700 text-slate-400"}`}>{l}</button>
          ))}
        </div>
        {!d ? <div className="text-slate-400"><Loader2 className="inline animate-spin" /> Memuat…</div> : d.label.length === 0 ? (
          <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6 text-center text-slate-400">{tab === "ANTRE" ? "Tidak ada label yang menunggu dicetak." : "Belum ada label yang dicetak."}</div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {d.label.map((l) => (
              <div key={l.id} data-testid={`label-${l.nomor_spk}`} className="flex flex-col gap-2 rounded-xl border border-slate-800 bg-slate-900/60 p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-mono text-lg font-bold text-slate-100">{l.nomor_spk}</div>
                    <div className="truncate font-semibold text-slate-200">{l.isi.pelanggan}</div>
                    <div className="text-xs text-slate-400">{GRUP[l.grup] || l.grup} · CS {l.isi.cs || "—"} · {W.waktuSedang(l.dibuat)}</div>
                  </div>
                  <button data-testid={`label-cetak-${l.nomor_spk}`} disabled={busy === l.id} onClick={() => cetakLabel(l)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
                    <Printer size={16} /> {tab === "ANTRE" ? "Cetak" : "Cetak ulang"}</button>
                </div>
                <ul className="text-sm text-slate-300">{(l.isi.jersey ? [`${l.isi.jersey.tim || "Jersey"} · ${l.isi.jersey.total_pcs} pcs`] : []).concat(l.isi.rincian).map((r) => <li key={r}>• {r}</li>)}</ul>
                {tab === "DICETAK" && <div className="text-xs text-slate-500">{infoCetak(l)}</div>}
              </div>
            ))}
          </div>
        )}
      </main>
      {cetak && <LabelCetak isi={cetak.isi} />}
    </div>
  );
}
